import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { STATUSES } from "@/pages/Bids";
import LetterPreview from "@/components/LetterPreview";
import {
  ArrowLeft, FileText, Download, Trash2, Sparkles, Upload, Loader2,
  FileSignature, CheckCheck, Wand2, Plus, Pencil, BookmarkPlus, Archive, X, ChevronDown, Award, Layers, Link2, Cpu, Image as ImageIcon,
} from "lucide-react";

const AI_MODELS = [
  { value: "gpt-4o", label: "GPT-4o — highest quality" },
  { value: "gpt-4o-mini", label: "GPT-4o mini — faster & cheaper" },
];

const SIG_OPTS = [
  { value: "for_company", label: "For Company (Signature with Seal)" },
  { value: "regards", label: "With Regards (signed letter)" },
];

const TOKENS = ["{company_name}", "{bid_number}", "{product_name}", "{quantity}", "{buyer_name}", "{udyam}", "{dpiit}", "{gstin}", "{bid_date}", "{signatory_name}"];

export default function BidWorkspace() {
  const { bidId } = useParams();
  const navigate = useNavigate();
  const [bid, setBid] = useState(null);
  const [docTypes, setDocTypes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selected, setSelected] = useState({});
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [atcText, setAtcText] = useState("");
  const [atcUrl, setAtcUrl] = useState("");
  const [aiModel, setAiModel] = useState("gpt-4o");
  const [atcLoading, setAtcLoading] = useState(false);
  const [autofilling, setAutofilling] = useState(false);
  const [certs, setCerts] = useState([]);
  const [certIds, setCertIds] = useState([]);
  const [docCertIds, setDocCertIds] = useState([]);
  const [stampPool, setStampPool] = useState([]);
  const fileRef = useRef();
  const productImgRef = useRef();
  const brochureRef = useRef();

  // template + edit dialogs
  const emptyTpl = { label: "", category: "Custom", has_title: true, title: "", subject: "", body: "", signature_style: "for_company" };
  const [tplOpen, setTplOpen] = useState(false);
  const [tplForm, setTplForm] = useState(emptyTpl);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [saveTplLabel, setSaveTplLabel] = useState("");

  const loadDocs = () => api.get(`/bids/${bidId}/documents`).then((r) => {
    setDocuments(r.data);
    setPreview((prev) => prev || (r.data.length ? r.data[0] : null));
  });
  const loadTypes = () => api.get("/doc-types").then((r) => setDocTypes(r.data));

  useEffect(() => {
    api.get(`/bids/${bidId}`).then((r) => { setBid(r.data); setCertIds(r.data.cert_ids || []); setAiModel(r.data.ai_model || "gpt-4o"); }).catch(() => navigate("/bids"));
    api.get("/certificates").then((r) => setCerts(r.data));
    api.get("/stamps").then((r) => setStampPool(r.data));
    loadTypes();
    loadDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidId]);

  const toggleCert = async (id) => {
    const next = certIds.includes(id) ? certIds.filter((x) => x !== id) : [...certIds, id];
    setCertIds(next);
    try { await api.put(`/bids/${bidId}/certificates`, { cert_ids: next }); }
    catch { toast.error("Could not update certificates"); }
  };

  useEffect(() => { setDocCertIds(preview?.cert_ids || []); }, [preview]);

  const toggleDocCert = async (id) => {
    if (!preview) return;
    const next = docCertIds.includes(id) ? docCertIds.filter((x) => x !== id) : [...docCertIds, id];
    setDocCertIds(next);
    try {
      await api.put(`/documents/${preview.id}/certificates`, { cert_ids: next });
      setPreview((p) => ({ ...p, cert_ids: next }));
      setDocuments((ds) => ds.map((d) => (d.id === preview.id ? { ...d, cert_ids: next } : d)));
    } catch { toast.error("Could not update certificates"); }
  };

  const allAttached = certs.length > 0 && certIds.length === certs.length;
  const attachAll = async () => {
    const next = allAttached ? [] : certs.map((c) => c.id);
    setCertIds(next);
    try {
      await api.put(`/bids/${bidId}/certificates`, { cert_ids: next });
      toast.success(allAttached ? "All certificates detached" : "All certificates attached");
    } catch { toast.error("Could not update certificates"); }
  };

  const grouped = useMemo(() => {
    const g = {};
    docTypes.forEach((d) => { (g[d.category] = g[d.category] || []).push(d); });
    return g;
  }, [docTypes]);

  const toggle = (key) => setSelected((s) => ({ ...s, [key]: !s[key] }));
  const selectedKeys = Object.keys(selected).filter((k) => selected[k]);

  const generate = async () => {
    if (selectedKeys.length === 0) return toast.error("Select at least one document");
    setGenerating(true);
    try {
      const r = await api.post(`/bids/${bidId}/documents`, { doc_types: selectedKeys });
      toast.success(`${r.data.length} document(s) generated`);
      setSelected({});
      if (r.data.length) setPreview(r.data[0]);
      loadDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const runAtc = async (file) => {
    setAtcLoading(true);
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      if (atcText.trim()) fd.append("text", atcText);
      if (atcUrl.trim()) fd.append("url", atcUrl.trim());
      fd.append("model", aiModel);
      const r = await api.post(`/bids/${bidId}/atc`, fd);
      toast.success("AI drafted your ATC compliance declaration");
      setPreview(r.data);
      setAtcText("");
      setAtcUrl("");
      loadDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI analysis failed");
    } finally {
      setAtcLoading(false);
    }
  };

  const changeModel = async (m) => {
    setAiModel(m);
    try { await api.put(`/bids/${bidId}/ai-model`, { ai_model: m }); } catch { /* non-critical */ }
  };

  const onProductImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !preview) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/documents/${preview.id}/product-image`, fd);
      setPreview(r.data);
      toast.success("Product image attached");
      loadDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not attach image");
    }
  };

  const removeProductImage = async () => {
    if (!preview) return;
    try {
      const r = await api.delete(`/documents/${preview.id}/product-image`);
      setPreview(r.data);
      toast.success("Product image removed");
      loadDocs();
    } catch { toast.error("Could not remove image"); }
  };

  const onAutofillSpecs = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !preview) return;
    setAutofilling(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("model", aiModel);
      const r = await api.post(`/documents/${preview.id}/autofill-specs`, fd);
      setPreview(r.data);
      const u = r.data.ai_usage;
      toast.success(u ? `Specs auto-filled · ${u.total_tokens.toLocaleString()} tok · ~$${u.cost_usd < 0.01 ? u.cost_usd.toFixed(4) : u.cost_usd.toFixed(3)}` : "Specs auto-filled");
      loadDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI autofill failed");
    } finally {
      setAutofilling(false);
    }
  };

  const onFile = (e) => { const f = e.target.files?.[0]; if (f) runAtc(f); e.target.value = ""; };
  const download = (id, format) => window.open(`${API}/documents/${id}/download?format=${format}`, "_blank");
  const downloadAll = (format) => window.open(`${API}/bids/${bidId}/download-all?format=${format}`, "_blank");
  const downloadMerged = () => window.open(`${API}/bids/${bidId}/merged.pdf`, "_blank");

  const changeStatus = async (status) => {
    try {
      await api.put(`/bids/${bidId}/status`, { status });
      setBid((b) => ({ ...b, status }));
      toast.success(`Marked as ${status}`);
    } catch { toast.error("Could not update status"); }
  };

  const remove = async (id) => {
    await api.delete(`/documents/${id}`);
    if (preview?.id === id) setPreview(null);
    toast.success("Document removed");
    loadDocs();
  };

  // ---- custom template create ----
  const saveTemplate = async () => {
    if (!tplForm.label.trim()) return toast.error("Template name required");
    if (!tplForm.body.trim()) return toast.error("Body text required");
    try {
      await api.post("/templates", tplForm);
      toast.success("Template saved — available for every bid");
      setTplOpen(false);
      setTplForm(emptyTpl);
      loadTypes();
    } catch { toast.error("Could not save template"); }
  };

  const deleteTemplate = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    await api.delete(`/templates/${id}`);
    toast.success("Template deleted");
    loadTypes();
  };

  // ---- inline edit ----
  const openEdit = () => {
    if (!preview) return;
    const l = preview.letter;
    setEditForm({
      title: l.title || "",
      subject: l.subject || "",
      salutation: l.salutation || "",
      body: (l.paragraphs || []).join("\n\n"),
      after: (l.paragraphs_after || []).join("\n\n"),
      signature_style: l.signature_style || "for_company",
      hasTable: !!l.table,
      table: l.table ? { headers: [...l.table.headers], rows: l.table.rows.map((r) => [...r]) } : null,
    });
    setEditOpen(true);
  };

  const setCell = (ri, ci, v) => setEditForm((f) => {
    const rows = f.table.rows.map((r) => [...r]);
    rows[ri][ci] = v;
    return { ...f, table: { ...f.table, rows } };
  });
  const addRow = () => setEditForm((f) => ({
    ...f, table: { ...f.table, rows: [...f.table.rows, f.table.headers.map((_, i) => (i === 0 ? String(f.table.rows.length + 1) : ""))] },
  }));
  const delRow = (ri) => setEditForm((f) => ({ ...f, table: { ...f.table, rows: f.table.rows.filter((_, i) => i !== ri) } }));
  const setHeader = (ci, v) => setEditForm((f) => {
    const headers = [...f.table.headers]; headers[ci] = v;
    return { ...f, table: { ...f.table, headers } };
  });
  const addColumn = () => setEditForm((f) => ({
    ...f, table: { headers: [...f.table.headers, "New Column"], rows: f.table.rows.map((r) => [...r, ""]) },
  }));
  const delColumn = (ci) => setEditForm((f) => {
    if (f.table.headers.length <= 1) return f;
    return {
      ...f,
      table: {
        headers: f.table.headers.filter((_, i) => i !== ci),
        rows: f.table.rows.map((r) => r.filter((_, i) => i !== ci)),
      },
    };
  });

  const saveEdit = async () => {
    const payload = {
      title: editForm.title || null,
      subject: editForm.subject,
      salutation: editForm.salutation || null,
      paragraphs: editForm.body.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean),
      paragraphs_after: editForm.after.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean),
      signature_style: editForm.signature_style,
    };
    if (editForm.hasTable && editForm.table) payload.table = editForm.table;
    try {
      const r = await api.put(`/documents/${preview.id}`, payload);
      setPreview(r.data);
      setEditOpen(false);
      toast.success("Document updated");
      loadDocs();
    } catch { toast.error("Could not save edits"); }
  };

  const saveBackToTemplate = async () => {
    if (!saveTplLabel.trim()) return toast.error("Give the template a name");
    try {
      await api.post(`/templates/from-document/${preview.id}`, { label: saveTplLabel });
      toast.success("Saved to your templates for next time");
      setSaveTplOpen(false);
      setSaveTplLabel("");
      loadTypes();
    } catch { toast.error("Could not save template"); }
  };

  if (!bid) return <div className="p-12 text-slate-500">Loading…</div>;

  return (
    <div className="px-4 lg:px-8 py-6 lg:py-8">
      <button onClick={() => navigate("/bids")} className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900 transition-colors" data-testid="back-to-bids">
        <ArrowLeft className="h-4 w-4 mr-1" /> All bids
      </button>

      <div className="mt-3 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight">{bid.product_name || "Bid"}</h1>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-slate-500">
            <span className="font-mono">{bid.bid_number}</span>
            <span>•</span><span>{bid.buyer_name}</span>
            <span>•</span><span>Qty {bid.quantity}</span>
            <span>•</span><span>{bid.bid_date}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Select value={bid.status || "Draft"} onValueChange={changeStatus}>
            <SelectTrigger className="w-[130px] rounded-full" data-testid="workspace-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.value}</SelectItem>)}
            </SelectContent>
          </Select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button disabled={documents.length === 0 && certIds.length === 0} className="rounded-full bg-emerald-600 hover:bg-emerald-500" data-testid="download-all-btn">
                <Archive className="h-4 w-4 mr-2" /> Download ({documents.length + certIds.length}) <ChevronDown className="h-4 w-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => downloadAll("both")} data-testid="dl-all-both">ZIP — PDF + Word</DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadAll("pdf")} data-testid="dl-all-pdf">ZIP — PDF only</DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadAll("docx")} data-testid="dl-all-docx">ZIP — Word only</DropdownMenuItem>
              <DropdownMenuItem onClick={downloadMerged} data-testid="dl-merge">
                <Layers className="h-4 w-4 mr-2" /> Merge everything into one PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 mt-6">
        {/* LEFT: templates + generated list */}
        <div className="xl:col-span-3 space-y-6">
          <Card className="p-5 rounded-xl border-border">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <FileSignature className="h-4 w-4 text-emerald-700" />
                <h3 className="font-head font-bold">Master Templates</h3>
              </div>
              <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-700" onClick={() => { setTplForm(emptyTpl); setTplOpen(true); }} data-testid="new-template-btn">
                <Plus className="h-4 w-4 mr-1" /> New
              </Button>
            </div>
            <div className="space-y-4 max-h-[360px] overflow-y-auto scrollbar-thin pr-1">
              {Object.entries(grouped).map(([cat, items]) => (
                <div key={cat}>
                  <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-400 mb-1.5">{cat}</div>
                  <div className="space-y-1">
                    {items.map((d) => (
                      <label key={d.key} data-testid={`tpl-${d.key}`} className="group flex items-center gap-2.5 rounded-lg px-2 py-2 hover:bg-slate-50 cursor-pointer">
                        <Checkbox checked={!!selected[d.key]} onCheckedChange={() => toggle(d.key)} data-testid={`tpl-check-${d.key}`} />
                        <span className="text-sm flex-1">{d.label}</span>
                        {d.is_custom && (
                          <button onClick={(e) => deleteTemplate(e, d.id)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-600" data-testid={`tpl-delete-${d.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <Button onClick={generate} disabled={generating} className="w-full mt-4 rounded-lg bg-slate-900 hover:bg-slate-800" data-testid="generate-selected-btn">
              {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
              Generate {selectedKeys.length > 0 ? `(${selectedKeys.length})` : ""}
            </Button>
          </Card>

          <Card className="p-5 rounded-xl border-border">
            <h3 className="font-head font-bold mb-3">Generated Documents</h3>
            <div className="space-y-2 max-h-[320px] overflow-y-auto scrollbar-thin">
              {documents.length === 0 && <div className="text-xs text-slate-400 py-6 text-center">Nothing generated yet.</div>}
              {documents.map((d) => (
                <div
                  key={d.id}
                  data-testid={`doc-item-${d.id}`}
                  onClick={() => setPreview(d)}
                  className={`rounded-lg border p-3 cursor-pointer transition-colors ${preview?.id === d.id ? "border-emerald-500 bg-emerald-50" : "border-border hover:bg-slate-50"}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2 min-w-0">
                      {d.source === "atc" ? <Sparkles className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" /> : <FileText className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />}
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{d.label}</div>
                        {d.source === "atc" && <Badge className="mt-1 bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]">AI drafted</Badge>}
                        {d.source === "custom" && <Badge className="mt-1 bg-slate-100 text-slate-600 hover:bg-slate-100 text-[10px]">Custom</Badge>}
                        {d.cert_ids?.length > 0 && <Badge className="mt-1 ml-1 bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">+{d.cert_ids.length} cert</Badge>}
                      </div>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); remove(d.id); }} className="text-slate-300 hover:text-red-600" data-testid={`doc-delete-${d.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-5 rounded-xl border-border">
            <div className="flex items-center gap-2 mb-3">
              <Award className="h-4 w-4 text-emerald-700" />
              <h3 className="font-head font-bold">Attach Certificates</h3>
              {certIds.length > 0 && <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 text-[10px] ml-auto">{certIds.length} attached</Badge>}
            </div>
            {certs.length > 0 && (
              <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 mb-2">
                <span className="text-xs font-medium text-slate-700">Attach all certificates</span>
                <Switch checked={allAttached} onCheckedChange={attachAll} data-testid="attach-all-switch" />
              </div>
            )}
            <div className="space-y-1 max-h-[300px] overflow-y-auto scrollbar-thin">
              {certs.length === 0 && (
                <div className="text-xs text-slate-400 py-4 text-center">
                  No certificates yet. Add them in <span className="text-emerald-700 cursor-pointer" onClick={() => navigate("/certificates")}>Certificate Library</span>.
                </div>
              )}
              {certs.map((c) => (
                <label key={c.id} data-testid={`cert-attach-${c.id}`} className="flex items-center gap-2.5 rounded-lg px-2 py-2 hover:bg-slate-50 cursor-pointer">
                  <Checkbox checked={certIds.includes(c.id)} onCheckedChange={() => toggleCert(c.id)} data-testid={`cert-check-${c.id}`} />
                  <span className="text-sm flex-1 truncate">{c.name}</span>
                  <span className="text-[10px] text-slate-400 uppercase">{c.ext}</span>
                </label>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-2">Attached certificates are included in the ZIP and the merged PDF.</p>
          </Card>
        </div>
        <div className="xl:col-span-6">
          <Card className="p-5 rounded-xl border-border bg-slate-50/60">
            <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-head font-bold">{preview ? preview.label : "Document Preview"}</h3>
                {preview?.ai_usage && (
                  <span
                    data-testid="ai-cost-meter"
                    title={`${preview.ai_usage.prompt_tokens} in + ${preview.ai_usage.completion_tokens} out tokens`}
                    className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 text-[11px] font-medium"
                  >
                    <Cpu className="h-3 w-3" />
                    {preview.ai_usage.model} · {preview.ai_usage.total_tokens.toLocaleString()} tok · ~${preview.ai_usage.cost_usd < 0.01 ? preview.ai_usage.cost_usd.toFixed(4) : preview.ai_usage.cost_usd.toFixed(3)}
                  </span>
                )}
              </div>
              {preview && (
                <div className="flex gap-2 flex-wrap">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="outline" className="rounded-lg" data-testid="doc-certs-btn">
                        <Award className="h-4 w-4 mr-1.5" /> Certificates{docCertIds.length ? ` (${docCertIds.length})` : ""} <ChevronDown className="h-3.5 w-3.5 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="max-h-[320px] overflow-y-auto w-64">
                      {certs.length === 0 && <div className="px-2 py-3 text-xs text-slate-400 text-center">No certificates in library</div>}
                      {certs.map((c) => (
                        <DropdownMenuItem
                          key={c.id}
                          onSelect={(e) => { e.preventDefault(); toggleDocCert(c.id); }}
                          className="gap-2 cursor-pointer"
                          data-testid={`doc-cert-${c.id}`}
                        >
                          <Checkbox checked={docCertIds.includes(c.id)} className="pointer-events-none" />
                          <span className="text-sm truncate">{c.name}</span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button size="sm" variant="outline" className="rounded-lg" onClick={openEdit} data-testid="edit-document-btn">
                    <Pencil className="h-4 w-4 mr-1.5" /> Edit
                  </Button>
                  {preview.doc_type === "product_catalogue" && (
                    <>
                      <input ref={brochureRef} type="file" accept=".pdf,.docx,.txt,.png,.jpg,.jpeg,.webp" hidden onChange={onAutofillSpecs} data-testid="autofill-specs-input" />
                      <Button size="sm" variant="outline" className="rounded-lg" onClick={() => brochureRef.current?.click()} disabled={autofilling} data-testid="autofill-specs-btn">
                        {autofilling ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Wand2 className="h-4 w-4 mr-1.5 text-amber-500" />} {autofilling ? "Reading…" : "Auto-fill Specs (AI)"}
                      </Button>
                      <input ref={productImgRef} type="file" accept=".png,.jpg,.jpeg,.webp,.gif" hidden onChange={onProductImage} data-testid="product-image-input" />
                      {preview.letter?.product_image_id ? (
                        <Button size="sm" variant="outline" className="rounded-lg" onClick={removeProductImage} data-testid="remove-product-image-btn">
                          <ImageIcon className="h-4 w-4 mr-1.5" /> Remove Image
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" className="rounded-lg" onClick={() => productImgRef.current?.click()} data-testid="attach-product-image-btn">
                          <ImageIcon className="h-4 w-4 mr-1.5" /> Attach Product Image
                        </Button>
                      )}
                    </>
                  )}
                  <Button size="sm" variant="outline" className="rounded-lg" onClick={() => { setSaveTplLabel(preview.label); setSaveTplOpen(true); }} data-testid="save-to-template-btn">
                    <BookmarkPlus className="h-4 w-4 mr-1.5" /> Save to Templates
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-lg" onClick={() => download(preview.id, "pdf")} data-testid="download-pdf-button">
                    <Download className="h-4 w-4 mr-1.5" /> PDF
                  </Button>
                  <Button size="sm" className="rounded-lg bg-slate-900 hover:bg-slate-800" onClick={() => download(preview.id, "docx")} data-testid="download-docx-button">
                    <Download className="h-4 w-4 mr-1.5" /> Word
                  </Button>
                </div>
              )}
            </div>
            {preview ? (
              <div className="max-w-[520px] mx-auto">
                <LetterPreview
                  doc={preview}
                  stampUrl={stampPool.length ? `${API}/stamps/${stampPool[(preview.stamp_seq || 0) % stampPool.length].id}/file` : null}
                />
              </div>
            ) : (
              <div className="a4-paper max-w-[520px] mx-auto bg-white paper-shadow rounded-sm flex items-center justify-center">
                <div className="text-center text-slate-400 px-8">
                  <FileText className="h-10 w-10 mx-auto mb-3" />
                  <p className="text-sm">Generate a document or run the AI ATC extractor to see a live letterhead preview here.</p>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* RIGHT: ATC AI */}
        <div className="xl:col-span-3">
          <Card className="p-5 rounded-xl border-border bg-slate-900 text-white">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-400" />
              <h3 className="font-head font-bold">AI ATC Extractor</h3>
            </div>
            <p className="text-xs text-slate-300 mt-2 leading-relaxed">
              Upload the buyer's ATC document (PDF, Word, or a screenshot/image), paste a public bid/ATC link, or paste
              its text. AI reads every clause and drafts a point-by-point acceptance declaration on your letterhead.
            </p>

            <div className="mt-4">
              <label className="flex items-center gap-1.5 text-[11px] text-slate-400 mb-1"><Cpu className="h-3.5 w-3.5" /> AI model (per bid)</label>
              <select
                data-testid="atc-model-select"
                value={aiModel}
                onChange={(e) => changeModel(e.target.value)}
                className="w-full rounded-lg bg-white/5 border border-white/10 text-white text-xs px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber-400"
              >
                {AI_MODELS.map((m) => <option key={m.value} value={m.value} className="text-slate-900">{m.label}</option>)}
              </select>
            </div>

            <input ref={fileRef} type="file" accept=".pdf,.txt,.docx,.png,.jpg,.jpeg,.webp" hidden onChange={onFile} data-testid="atc-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={atcLoading} className="w-full mt-4 rounded-lg bg-white/10 hover:bg-white/20 border-0 text-white" data-testid="atc-upload-button">
              <Upload className="h-4 w-4 mr-2" /> Upload ATC (PDF / Word / Image)
            </Button>

            <div className="text-center text-[11px] text-slate-400 my-3">or fetch from a link</div>
            <div className="flex gap-2">
              <input
                data-testid="atc-url-input"
                value={atcUrl}
                onChange={(e) => setAtcUrl(e.target.value)}
                placeholder="Paste public bid / ATC PDF link…"
                className="flex-1 rounded-lg bg-white/5 border border-white/10 text-white text-xs px-3 py-2 placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-amber-400"
              />
              <Button onClick={() => runAtc(null)} disabled={atcLoading || !atcUrl.trim()} className="rounded-lg bg-white/10 hover:bg-white/20 border-0 text-white shrink-0" data-testid="atc-fetch-url-button">
                {atcLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
              </Button>
            </div>

            <div className="text-center text-[11px] text-slate-400 my-3">or paste text</div>

            <Textarea data-testid="atc-text-input" value={atcText} onChange={(e) => setAtcText(e.target.value)} rows={6} placeholder="Paste the ATC clauses here…" className="resize-none bg-white/5 border-white/10 text-white placeholder:text-slate-500 text-xs" />
            <Button onClick={() => runAtc(null)} disabled={atcLoading || atcText.trim().length < 20} className="w-full mt-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 border-0" data-testid="atc-analyze-button">
              {atcLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCheck className="h-4 w-4 mr-2" />}
              {atcLoading ? "Analyzing…" : "Draft Compliance"}
            </Button>
          </Card>
        </div>
      </div>

      {/* New custom template dialog */}
      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Document Template</DialogTitle>
            <DialogDescription>Reusable across all bids. Use placeholders and they fill in automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto scrollbar-thin pr-1">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Template Name</Label>
              <Input data-testid="tpl-label-input" value={tplForm.label} onChange={(e) => setTplForm({ ...tplForm, label: e.target.value })} className="mt-1.5" placeholder="e.g. Authorization Letter" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
              <Label className="text-sm">Show centered title</Label>
              <Switch checked={tplForm.has_title} onCheckedChange={(v) => setTplForm({ ...tplForm, has_title: v })} data-testid="tpl-title-switch" />
            </div>
            {tplForm.has_title && (
              <div>
                <Label className="text-xs font-semibold text-slate-600">Title</Label>
                <Input data-testid="tpl-title-input" value={tplForm.title} onChange={(e) => setTplForm({ ...tplForm, title: e.target.value })} className="mt-1.5" placeholder="AUTHORIZATION LETTER" />
              </div>
            )}
            <div>
              <Label className="text-xs font-semibold text-slate-600">Subject</Label>
              <Input data-testid="tpl-subject-input" value={tplForm.subject} onChange={(e) => setTplForm({ ...tplForm, subject: e.target.value })} className="mt-1.5" placeholder="Authorization for {product_name}" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Body (separate paragraphs with a blank line)</Label>
              <Textarea data-testid="tpl-body-input" rows={6} value={tplForm.body} onChange={(e) => setTplForm({ ...tplForm, body: e.target.value })} className="mt-1.5 resize-none text-sm" placeholder="We, M/s {company_name}, in reference to Bid No. {bid_number}..." />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Signature Style</Label>
              <Select value={tplForm.signature_style} onValueChange={(v) => setTplForm({ ...tplForm, signature_style: v })}>
                <SelectTrigger className="mt-1.5" data-testid="tpl-sig-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SIG_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">Available placeholders</div>
              <div className="flex flex-wrap gap-1">
                {TOKENS.map((t) => <code key={t} className="text-[10px] bg-white border border-border rounded px-1.5 py-0.5 text-emerald-700">{t}</code>)}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTplOpen(false)} className="rounded-lg">Cancel</Button>
            <Button onClick={saveTemplate} className="rounded-lg bg-slate-900 hover:bg-slate-800" data-testid="tpl-save-btn">Save Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inline edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Document</DialogTitle>
            <DialogDescription>Changes apply to this document only. Downloads reflect your edits.</DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto scrollbar-thin pr-1">
              <div>
                <Label className="text-xs font-semibold text-slate-600">Title (leave blank for none)</Label>
                <Input data-testid="edit-title-input" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Subject</Label>
                <Input data-testid="edit-subject-input" value={editForm.subject} onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Salutation (optional)</Label>
                <Input data-testid="edit-salutation-input" value={editForm.salutation} onChange={(e) => setEditForm({ ...editForm, salutation: e.target.value })} className="mt-1.5" placeholder="Dear Sir/Madam," />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Body (blank line between paragraphs)</Label>
                <Textarea data-testid="edit-body-input" rows={8} value={editForm.body} onChange={(e) => setEditForm({ ...editForm, body: e.target.value })} className="mt-1.5 resize-none text-sm" />
              </div>
              {editForm.hasTable && editForm.table && (
                <div className="rounded-lg border border-border p-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-semibold text-slate-600">Compliance Table</Label>
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="outline" className="h-7 rounded-lg" onClick={addColumn} data-testid="edit-add-col-btn">
                        <Plus className="h-3.5 w-3.5 mr-1" /> Column
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 rounded-lg" onClick={addRow} data-testid="edit-add-row-btn">
                        <Plus className="h-3.5 w-3.5 mr-1" /> Row
                      </Button>
                    </div>
                  </div>
                  {/* editable headers */}
                  <div className="flex items-start gap-1.5 mb-2">
                    <div className="flex-1 grid gap-1" style={{ gridTemplateColumns: `repeat(${editForm.table.headers.length}, minmax(0,1fr))` }}>
                      {editForm.table.headers.map((h, ci) => (
                        <div key={ci} className="relative">
                          <Input
                            data-testid={`edit-header-${ci}`}
                            value={h}
                            onChange={(e) => setHeader(ci, e.target.value)}
                            className="h-7 text-[11px] px-1.5 font-semibold pr-6"
                          />
                          {editForm.table.headers.length > 1 && (
                            <button onClick={() => delColumn(ci)} className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-300 hover:text-red-600" data-testid={`edit-del-col-${ci}`}>
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="w-3.5 shrink-0" />
                  </div>
                  <div className="space-y-2 max-h-[240px] overflow-y-auto scrollbar-thin">
                    {editForm.table.rows.map((row, ri) => (
                      <div key={ri} className="flex items-start gap-1.5" data-testid={`edit-table-row-${ri}`}>
                        <div className="flex-1 grid gap-1" style={{ gridTemplateColumns: `repeat(${row.length}, minmax(0,1fr))` }}>
                          {row.map((cell, ci) => (
                            <Textarea
                              key={ci}
                              data-testid={`edit-cell-${ri}-${ci}`}
                              value={cell}
                              onChange={(e) => setCell(ri, ci, e.target.value)}
                              rows={2}
                              placeholder={editForm.table.headers[ci]}
                              className="resize-none text-[11px] p-1.5"
                            />
                          ))}
                        </div>
                        <button onClick={() => delRow(ri)} className="mt-2 text-slate-300 hover:text-red-600 shrink-0" data-testid={`edit-del-row-${ri}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1.5">Rename headers above, add or remove columns and rows.</div>
                </div>
              )}
              <div>
                <Label className="text-xs font-semibold text-slate-600">Closing paragraph(s) after table (optional)</Label>
                <Textarea data-testid="edit-after-input" rows={3} value={editForm.after} onChange={(e) => setEditForm({ ...editForm, after: e.target.value })} className="mt-1.5 resize-none text-sm" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Signature Style</Label>
                <Select value={editForm.signature_style} onValueChange={(v) => setEditForm({ ...editForm, signature_style: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="edit-sig-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{SIG_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-lg">Cancel</Button>
            <Button onClick={saveEdit} className="rounded-lg bg-slate-900 hover:bg-slate-800" data-testid="edit-save-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save-to-template dialog */}
      <Dialog open={saveTplOpen} onOpenChange={setSaveTplOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save as Reusable Template</DialogTitle>
            <DialogDescription>Your current wording is saved with placeholders re-inserted, ready for future bids.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Template Name</Label>
            <Input data-testid="save-tpl-label-input" value={saveTplLabel} onChange={(e) => setSaveTplLabel(e.target.value)} className="mt-1.5" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveTplOpen(false)} className="rounded-lg">Cancel</Button>
            <Button onClick={saveBackToTemplate} className="rounded-lg bg-slate-900 hover:bg-slate-800" data-testid="save-tpl-confirm-btn">Save Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
